/**
 * Manual structure name aliases for AdaptiveRT (canonical name ↔ local DVH labels per stage).
 */

/**
 * @typedef {{ order: number, name: string }} AdaptiveRtAliasMember
 * @typedef {{ canonicalName: string, members: AdaptiveRtAliasMember[] }} AdaptiveRtAliasGroup
 */

/**
 * @param {Array<{ canonical_name?: string, canonicalName?: string, members?: Array<{ order: number, name: string }> }>} raw
 * @returns {AdaptiveRtAliasGroup[]}
 */
export function normalizeAliasGroupsFromApi(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const canonicalName = (item.canonical_name || item.canonicalName || '').trim();
      const members = (item.members || [])
        .map((m) => ({
          order: Number(m.order),
          name: (m.name || '').trim(),
        }))
        .filter((m) => m.order >= 1 && m.name);
      return { canonicalName, members };
    })
    .filter((g) => g.canonicalName && g.members.length >= 2);
}

/**
 * @param {AdaptiveRtAliasGroup[]} groups
 */
export function aliasGroupsToApiPayload(groups) {
  return (groups || []).map((g) => ({
    canonical_name: g.canonicalName,
    members: g.members.map((m) => ({ order: m.order, name: m.name })),
  }));
}

/**
 * @param {string} canonicalName
 * @param {number} order
 * @param {AdaptiveRtAliasGroup[]} groups
 * @returns {string}
 */
export function resolveLocalStructureName(canonicalName, order, groups) {
  const canonical = (canonicalName || '').trim();
  if (!canonical) return '';
  for (const group of groups || []) {
    if (group.canonicalName !== canonical) continue;
    const member = group.members.find((m) => m.order === order);
    if (member) return member.name;
  }
  return canonical;
}

/**
 * @param {AdaptiveRtAliasGroup[]} groups
 * @param {number} order
 * @returns {string[]}
 */
export function localNamesUsedInOrder(groups, order) {
  const used = new Set();
  for (const g of groups || []) {
    const m = g.members.find((x) => x.order === order);
    if (m) used.add(m.name);
  }
  return [...used];
}
